import axios from 'axios';

/** Base path API — same-origin via CRA/nginx proxy. */
export const API = '/api';

export const apiClient = axios.create({
  baseURL: API,
  timeout: 120000,
});

let refreshPromise = null;

export function getAuthToken() {
  try {
    return localStorage.getItem('token') || '';
  } catch {
    return '';
  }
}

export function getRefreshToken() {
  try {
    return localStorage.getItem('refreshToken') || '';
  } catch {
    return '';
  }
}

export function setAuthSession({ token, refreshToken, user }) {
  try {
    if (token) localStorage.setItem('token', token);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    if (user) localStorage.setItem('user', JSON.stringify(user));
  } catch {
    /* ignore */
  }
}

export function clearAuthSession() {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  } catch {
    /* ignore */
  }
}

async function tryRefreshToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  try {
    const res = await fetch(`${API}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    setAuthSession(data);
    return true;
  } catch {
    return false;
  }
}

apiClient.interceptors.request.use((config) => {
  const token = getValidToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true;
      if (!refreshPromise) {
        refreshPromise = tryRefreshToken().finally(() => {
          refreshPromise = null;
        });
      }
      const ok = await refreshPromise;
      if (ok) {
        original.headers.Authorization = `Bearer ${getAuthToken()}`;
        return apiClient(original);
      }
      clearAuthSession();
      const onLogin = typeof window !== 'undefined' && window.location.pathname.includes('/login');
      if (!onLogin && typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

/** Headers untuk fetch(). */
export function authHeaders(extra = {}) {
  const token = getValidToken() || getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

/** @deprecated Gunakan apiClient — dipertahankan untuk kompatibilitas sementara. */
export function authConfig(extra = {}) {
  const token = getValidToken() || getAuthToken();
  return {
    ...extra,
    headers: {
      ...(extra.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
}

export function getCurrentUser() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const u = JSON.parse(raw);
    if (!u || typeof u !== 'object') return null;
    return { ...u, id: u.id || u._id || null, role: u.role || null };
  } catch {
    return null;
  }
}

export function isTokenValid(token) {
  if (!token || typeof token !== 'string') return false;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload.exp) return true;
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

export function getValidToken() {
  const token = getAuthToken();
  return isTokenValid(token) ? token : null;
}

/** Resolve URL media/upload — same-origin via nginx proxy ke backend. */
export function resolveMediaAssetUrl(url) {
  if (!url) return '';
  if (/^(blob:|data:)/.test(url)) return url;
  // Legacy absolute URL → path relatif same-origin
  const legacy = url.match(/^https?:\/\/(?:api\.)?digitalstorycerdas\.id(\/[^?#]*)/i);
  if (legacy) return legacy[1];
  if (/^https?:/.test(url)) return url;
  return url.startsWith('/') ? url : `/${url}`;
}

/** fetch() wrapper dengan auth + penanganan 401 terpusat. */
export async function apiFetch(path, options = {}) {
  let res = await fetch(`${API}${path}`, {
    ...options,
    headers: { ...authHeaders(), ...(options.headers || {}) },
  });
  if (res.status === 401 && !options._retry) {
    const ok = await tryRefreshToken();
    if (ok) {
      return apiFetch(path, { ...options, _retry: true });
    }
    clearAuthSession();
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
  }
  return res;
}

/** Ambil data per-user dari backend (Asah, Eksplorasi, dll). */
export async function fetchUserData(key) {
  const { data } = await apiClient.get(`/user-data/${key}`);
  return data;
}

/** Simpan data per-user ke backend. */
export async function saveUserData(key, payload) {
  const { data } = await apiClient.put(`/user-data/${key}`, { data: payload });
  return data;
}

export default apiClient;
